import { useEffect, useRef, useState } from 'react'
import { V3_INPUT_CLS, V3_SECONDARY_BUTTON_CLS } from './controlStyles'
import { planningDay as day, parsePlanningDate as dateNumber, formatPlanningDate as dateText, planningYearEnd } from './planningDates'

export interface PlanningTimelineDefinition {
    selectedIdField?: string
    rowsField: string
    idField: string
    labelField: string
    dateField: string
    quantityField: string
    fromField: string
    toField: string
    editableField?: string
    statusField?: string
    noteField?: string
    dateEditorLabel?: string
    searchLabel?: string
    disabledField?: string
    previousLabel?: string
    nextLabel?: string
}

const displayDate = (value: number) => new Date(value).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: '2-digit', timeZone: 'UTC' })

/** A dated draft editor. Hosts own permissions, calculation, persistence and undo. */
export function PlanningTimeline({ definition: d, record, onChange, onSelect, label }: {
    definition: PlanningTimelineDefinition
    record: Record<string, unknown>
    label?: string | undefined
    onSelect?: ((id: string | null) => void) | undefined
    onChange?: ((rows: Record<string, unknown>[]) => void) | undefined
}) {
    const [search, setSearch] = useState('')
    const [page, setPage] = useState(0)
    const [localSelected, setLocalSelected] = useState<string | null>(null)
    const selected = d.selectedIdField && onSelect ? record[d.selectedIdField] : localSelected
    function select(id: string | null) { setLocalSelected(id); onSelect?.(id) }
    const [zoom, setZoom] = useState<1 | 7 | 30 | 90 | 365>(365)
    const [offset, setOffset] = useState(0)
    const start = dateNumber(record[d.fromField])
    const end = dateNumber(record[d.toField])
    const annualEnd = start === null ? null : planningYearEnd(start)
    const yearSpan = start === null || annualEnd === null ? 365 : Math.round((annualEnd - start) / day)
    const raw = record[d.rowsField]
    const previousSelection = useRef<unknown>(null)
    const details = useRef<HTMLDivElement>(null)
    useEffect(() => {
        if (selected === previousSelection.current) return
        previousSelection.current = selected
        if (typeof selected !== 'string' || !Array.isArray(raw) || start === null || end === null) return
        const index = raw.findIndex(row => row && typeof row === 'object' && row[d.idField] === selected)
        if (index < 0) return
        setSearch('')
        setPage(Math.floor(index / 25))
        const date = dateNumber(raw[index][d.dateField])
        const range = Math.max(0, Math.round((end - start) / day))
        const span = Math.min(zoom === 365 ? yearSpan : zoom, range)
        const currentOffset = Math.min(offset, Math.max(0, range - span))
        if (date !== null && (date < start + currentOffset * day || date > start + (currentOffset + span) * day)) {
            setOffset(Math.max(0, Math.min(range - span, Math.floor((date - start) / day))))
        }
        details.current?.scrollIntoView?.({ block: 'nearest' })
    }, [selected, raw, d.idField, d.dateField, start, end, zoom, offset, yearSpan])
    const disabled = !!d.disabledField && record[d.disabledField] === true
    const rows = Array.isArray(raw) ? raw.filter((r): r is Record<string, unknown> => !!r && typeof r === 'object' && !Array.isArray(r)) : []
    if (start === null || end === null || annualEnd === null || end < start || end > annualEnd) {
        return <p role="alert">Choose a valid planning window of at most 12 months.</p>
    }
    if (!Array.isArray(raw) || rows.length !== raw.length || rows.length > 10_000
        || rows.some(r => typeof r[d.idField] !== 'string' || !r[d.idField])
        || new Set(rows.map(r => r[d.idField])).size !== rows.length) {
        return <p role="alert">Planning rows are invalid, duplicated or exceed the 10,000-row limit. Narrow or refresh the source.</p>
    }
    const range = Math.round((end - start) / day)
    const span = Math.min(zoom === 365 ? yearSpan : zoom, range)
    const viewOffset = Math.min(offset, Math.max(0, range - span))
    const first = start + viewOffset * day
    const last = first + span * day
    const filtered = rows.filter(r => String(r[d.labelField] ?? '').toLowerCase().includes(search.toLowerCase()))
    const currentPage = Math.min(page, Math.max(0, Math.ceil(filtered.length / 25) - 1))
    const visible = filtered.slice(currentPage * 25, (currentPage + 1) * 25)
    const chosen = rows.find(r => r[d.idField] === selected)
    const editable = (row: Record<string, unknown>) => !!onChange && !!d.editableField && row[d.editableField] === true
    function move(id: string, value: string) {
        const target = rows.find(r => r[d.idField] === id)
        const date = dateNumber(value)
        if (disabled || !target || !editable(target) || date === null || date < start! || date > annualEnd!) return
        onChange?.(rows.map(r => r === target ? { ...r, [d.dateField]: value } : r))
    }
    return <section aria-label={label ?? 'Planning timeline'} className="space-y-3 text-sm text-neutral-900 dark:text-slate-100">
        <div className="flex flex-wrap items-center gap-3">
            <div><input aria-label={d.searchLabel ?? 'Search timeline'} value={search} onChange={e => { setSearch(e.target.value); setPage(0) }} className={V3_INPUT_CLS} /></div>
            <label className="flex items-center gap-2">View <select className={V3_INPUT_CLS} aria-label="Timeline zoom" value={zoom} onChange={e => { setZoom(Number(e.target.value) as 1 | 7 | 30 | 90 | 365); setOffset(0) }}>
                <option value={1}>Day</option><option value={7}>Week</option><option value={30}>Month</option><option value={90}>Quarter</option><option value={365}>Year</option>
            </select></label>
            <button type="button" className={V3_SECONDARY_BUTTON_CLS} disabled={first <= start} onClick={() => setOffset(Math.max(0, viewOffset - span))}>Earlier</button>
            <span>{dateText(first)} – {dateText(last)}</span>
            <button type="button" className={V3_SECONDARY_BUTTON_CLS} disabled={last >= end} onClick={() => setOffset(Math.min(range - span, viewOffset + span))}>Later</button>
        </div>
        <div className="overflow-x-auto"><div className="min-w-[600px]">
            {visible.map(row => {
                const id = String(row[d.idField])
                const date = dateNumber(row[d.dateField])
                const position = date === null ? 0 : Math.max(0, Math.min(100, (date - first) / Math.max(day, last - first) * 100))
                const label = String(row[d.labelField] ?? id)
                const suffix = date === null ? 'Date needed' : `${displayDate(date)}${date < start ? ' · overdue' : date < first ? ' · before view' : date > last ? ' · beyond view' : ''}`
                return <div key={id} className="grid grid-cols-[12rem_1fr] border-t border-neutral-200 dark:border-slate-700 min-h-20">
                    <div className="p-3 break-words">{label}{d.statusField && <span className="block text-xs text-neutral-500 dark:text-slate-400">{String(row[d.statusField] ?? '')}</span>}</div>
                    <div className="relative border-l border-neutral-200 dark:border-slate-700" role="group" aria-label={`Timeline for ${label}`}
                        onDragOver={e => { if (onChange) e.preventDefault() }} onDrop={e => {
                            e.preventDefault()
                            const bounds = e.currentTarget.getBoundingClientRect()
                            if (bounds.width <= 0 || !Number.isFinite(e.clientX)) return
                            const date = first + Math.round(Math.max(0, Math.min(1, (e.clientX - bounds.left) / bounds.width)) * span) * day
                            move(e.dataTransfer.getData('application/x-marque-planning-row'), dateText(date))
                        }}>
                        <button type="button" disabled={disabled} draggable={editable(row) && !disabled} onDragStart={e => { e.dataTransfer.setData('application/x-marque-planning-row', id); e.dataTransfer.effectAllowed = 'move' }}
                            aria-label={`${editable(row) ? 'Move' : 'Inspect'} ${label}, ${String(row[d.quantityField] ?? '')}, ${suffix}`}
                            onClick={() => select(id)} onKeyDown={e => {
                                if (editable(row) && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
                                    e.preventDefault()
                                    move(id, dateText((date ?? first) + (e.key === 'ArrowRight' ? day : -day)))
                                }
                            }} style={{ left: `${position}%`, transform: position > 80 ? 'translateX(-100%)' : undefined }}
                            className={`absolute top-3 rounded px-3 py-2 text-sm text-left disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${editable(row) ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-neutral-100 text-neutral-900 dark:bg-slate-700 dark:text-slate-100'}`}>
                            <span className="font-semibold">{String(row[d.quantityField] ?? '')}</span><span className="block text-xs">{suffix}</span>
                        </button>
                    </div>
                </div>
            })}
            {!visible.length && <p className="p-5">No matching planning rows.</p>}
        </div></div>
        <div className="flex justify-end gap-3"><span>{filtered.length ? currentPage * 25 + 1 : 0}–{Math.min((currentPage + 1) * 25, filtered.length)} of {filtered.length}</span>
            <button type="button" className={V3_SECONDARY_BUTTON_CLS} disabled={currentPage === 0} onClick={() => setPage(currentPage - 1)}>{d.previousLabel ?? 'Previous rows'}</button>
            <span>Page {currentPage + 1}</span>
            <button type="button" className={V3_SECONDARY_BUTTON_CLS} disabled={(currentPage + 1) * 25 >= filtered.length} onClick={() => setPage(currentPage + 1)}>{d.nextLabel ?? 'Next rows'}</button>
        </div>
        {chosen && <div ref={details} className="flex flex-wrap items-center gap-3 border border-neutral-200 dark:border-slate-700 rounded p-3">
            <span className="font-semibold">{String(chosen[d.labelField])}</span>
            {d.noteField && <span>{String(chosen[d.noteField] ?? '')}</span>}
            <label>{d.dateEditorLabel ?? 'Date'} <input className={V3_INPUT_CLS} type="date" aria-label={d.dateEditorLabel ?? 'Selected planning date'} min={dateText(start)} max={dateText(annualEnd)}
                value={dateNumber(chosen[d.dateField]) === null ? '' : String(chosen[d.dateField])} disabled={disabled || !editable(chosen)}
                onChange={e => move(String(chosen[d.idField]), e.target.value)} /></label>
            {!editable(chosen) && <span>Inspect only</span>}
            <button type="button" className={V3_SECONDARY_BUTTON_CLS} onClick={() => select(null)}>Close details</button>
        </div>}
    </section>
}
